import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import QRCode from "qrcode";
import { montarPixCopiaCola } from "@/lib/pix";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import {
  FileText,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Printer,
  Trash2,
  MoreVertical,
  Car,
  PackageSearch,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Empresa = {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  cnpj: string | null;
  cidade?: string;
};

type LaudoDisponivel = {
  id: string;
  laudoId: string | null;
  status: string;
  dataVistoria: string | null;
  veiculo?: { placa: string; tipo: string } | null;
};

type FaturaParceiroItem = {
  id: string;
  laudoIdTexto: string | null;
  placa: string | null;
  tipoVeiculo: string;
  valorUnitario: string;
  descricao: string | null;
};

type FaturaParceiro = {
  id: string;
  numero: number;
  mes: number;
  ano: number;
  status: string;
  valorTotal: string;
  vencimento: string;
  observacoes: string | null;
  chavePix: string | null;
  pagoEm: string | null;
  criadoEm: string;
  vistoriadora?: Empresa;
  parceiro?: Empresa;
  itens?: FaturaParceiroItem[];
};

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const TIPO_VEICULO_LABELS: Record<string, string> = {
  auto_leve: "Auto Leve",
  moto: "Moto",
  caminhao: "Caminhão",
  onibus: "Ônibus",
};

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: React.ElementType;
  }
> = {
  pendente: { label: "Pendente", variant: "secondary", icon: Clock },
  pago: { label: "Pago", variant: "default", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", variant: "outline", icon: XCircle },
};

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function FaturaDetalheDialog({
  fatura,
  onClose,
  onVerRelatorio,
}: {
  fatura: FaturaParceiro | null;
  onClose: () => void;
  onVerRelatorio: (id: string) => void;
}) {
  const [pixQrDataUrl, setPixQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fatura?.chavePix || !fatura.vistoriadora) {
      setPixQrDataUrl(null);
      return;
    }
    const payload = montarPixCopiaCola({
      chave: fatura.chavePix,
      nomeRecebedor: fatura.vistoriadora.nomeFantasia || fatura.vistoriadora.razaoSocial,
      cidade: fatura.vistoriadora.cidade ?? "",
      valor: Number(fatura.valorTotal),
    });
    QRCode.toDataURL(payload, { margin: 1, width: 160 })
      .then(setPixQrDataUrl)
      .catch(() => setPixQrDataUrl(null));
  }, [fatura]);

  if (!fatura) return null;
  const itens = fatura.itens ?? [];

  return (
    <Dialog open={!!fatura} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            FP-{fatura.ano}-{String(fatura.numero).padStart(4, "0")}
          </DialogTitle>
          <DialogDescription>
            {MESES[fatura.mes - 1]} {fatura.ano} · Vencimento: {formatDate(fatura.vencimento)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <span className="font-medium">Total ({itens.length} laudo{itens.length !== 1 ? "s" : ""})</span>
            <span className="text-xl font-bold">{formatCurrency(Number(fatura.valorTotal))}</span>
          </div>

          {itens.length > 0 && (
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {itens.map((item) => (
                <div key={item.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <span className="font-mono font-medium">{item.placa || "—"}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {TIPO_VEICULO_LABELS[item.tipoVeiculo] || item.tipoVeiculo}
                      {item.laudoIdTexto && <> · {item.laudoIdTexto}</>}
                    </span>
                  </div>
                  <span className="font-semibold">{formatCurrency(Number(item.valorUnitario))}</span>
                </div>
              ))}
            </div>
          )}

          {fatura.chavePix && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">
                  Pagamento via PIX
                </p>
                <p className="text-sm font-mono font-semibold text-slate-800 break-all">
                  {fatura.chavePix}
                </p>
              </div>
              {pixQrDataUrl && (
                <img
                  src={pixQrDataUrl}
                  alt="QR Code PIX"
                  className="w-24 h-24 shrink-0 rounded-lg border border-emerald-200 bg-white p-1"
                />
              )}
            </div>
          )}

          {fatura.observacoes && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{fatura.observacoes}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <Button className="gap-2" onClick={() => onVerRelatorio(fatura.id)}>
            <Printer className="w-4 h-4" />
            Ver relatório completo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NovaFaturaDialog({
  open,
  onClose,
  parceiros,
  onSave,
  loading,
  initialLoteId,
  initialParceiroId,
}: {
  open: boolean;
  onClose: () => void;
  parceiros: Empresa[];
  onSave: (data: any) => void;
  loading: boolean;
  initialLoteId?: string;
  initialParceiroId?: string;
}) {
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;

  const [parceiroId, setParceiroId] = useState(initialParceiroId ?? "");
  const [mes, setMes] = useState(String(mesAtual));
  const [ano, setAno] = useState(String(anoAtual));
  const [vencimento, setVencimento] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });
  const [observacoes, setObservacoes] = useState("");
  const [valorPadrao, setValorPadrao] = useState("");
  const [selecionados, setSelecionados] = useState<Record<string, string>>({}); // laudoId -> valor

  // Chave PIX da vistoriadora (configurada em Configurações) — pré-preenche a fatura
  const { data: pixConfig } = useQuery<{ chavePix: string | null }>({
    queryKey: ["/api/configuracoes/chave-pix"],
    queryFn: () => apiRequest("GET", "/api/configuracoes/chave-pix").then((r) => r.json()),
  });
  const [chavePix, setChavePix] = useState("");
  useEffect(() => {
    if (pixConfig?.chavePix) setChavePix(pixConfig.chavePix);
  }, [pixConfig]);

  const { data: laudosDisponiveis = [], isLoading: loadingLaudos } = useQuery<
    LaudoDisponivel[]
  >({
    queryKey: ["laudos-disponiveis-faturamento", parceiroId],
    queryFn: async () => {
      try {
        const res = await apiRequest(
          "GET",
          `/api/faturas-parceiro/laudos-disponiveis?parceiro_id=${parceiroId}`,
        );
        return res.json();
      } catch {
        return [];
      }
    },
    enabled: !!parceiroId,
  });

  // Configuração de preços para o parceiro selecionado
  const { data: precosConfig } = useQuery<{
    valorPorLaudo: string | null;
    valoresPorTipo: Record<string, number> | null;
  } | null>({
    queryKey: ["precos-para-parceiro", parceiroId],
    queryFn: () =>
      apiRequest("GET", `/api/precos-vistoriadora/para-parceiro?parceiro_id=${parceiroId}`).then((r) => r.json()),
    enabled: !!parceiroId,
  });

  // Modo de Faturamento para Parceiros (define se faz sentido importar de um lote)
  const { data: configFaturamento } = useQuery<{ modeFaturamento: string }>({
    queryKey: ["configuracoes-faturamento"],
    queryFn: () => apiRequest("GET", "/api/configuracoes").then((r) => r.json()),
  });
  const modoPorLaudo = (configFaturamento?.modeFaturamento ?? "por_laudo") === "por_laudo";

  // Lotes fechados por parceiro (para importar laudos) — não se aplica no modo "Por Laudo",
  // onde cada laudo já vira fatura individualmente ao ser finalizado.
  const { data: lotesDosParceiro = [] } = useQuery<
    { id: string; mes: number; ano: number; qtdLaudos: number }[]
  >({
    queryKey: ["lotes-fechados-parceiro", parceiroId],
    queryFn: () =>
      apiRequest("GET", `/api/lotes?vistoriadoraId=me&parceiroId=${parceiroId}&fechado=true`).then((r) => r.json()).catch(() => []),
    enabled: !!parceiroId && !modoPorLaudo,
  });

  function getPrecoParaTipo(tipo: string | undefined): string {
    if (!precosConfig) return "";
    if (precosConfig.valoresPorTipo && tipo && precosConfig.valoresPorTipo[tipo] != null)
      return String(precosConfig.valoresPorTipo[tipo]);
    if (precosConfig.valorPorLaudo) return String(precosConfig.valorPorLaudo);
    return "";
  }

  // Se veio de um lote, pré-seleciona os laudos disponíveis desse lote
  const [loteImportadoId, setLoteImportadoId] = useState(initialLoteId);
  const { data: laudosDoLote } = useQuery<LaudoDisponivel[]>({
    queryKey: ["laudos-do-lote", loteImportadoId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/lotes/${loteImportadoId}/laudos`);
      return res.json();
    },
    enabled: !!loteImportadoId,
  });

  useEffect(() => {
    if (laudosDoLote && laudosDoLote.length > 0) {
      const preSelected: Record<string, string> = {};
      laudosDoLote.forEach((l) => { preSelected[l.id] = getPrecoParaTipo(l.veiculo?.tipo); });
      setSelecionados(preSelected);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laudosDoLote]);

  function handleToggleLaudo(laudo: LaudoDisponivel) {
    setSelecionados((prev) => {
      if (prev[laudo.id] !== undefined) {
        const next = { ...prev };
        delete next[laudo.id];
        return next;
      }
      // Prioridade: valor padrão manual > config por tipo > valorPorLaudo padrão
      const autoValor = valorPadrao || getPrecoParaTipo(laudo.veiculo?.tipo);
      return { ...prev, [laudo.id]: autoValor };
    });
  }

  function handleValorItem(laudoId: string, valor: string) {
    setSelecionados((prev) => ({ ...prev, [laudoId]: valor }));
  }

  function aplicarValorPadrao() {
    if (!valorPadrao) return;
    setSelecionados((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = valorPadrao;
      });
      return next;
    });
  }

  const valorTotal = Object.values(selecionados).reduce(
    (acc, v) => acc + (Number(v) || 0),
    0,
  );
  const anos = Array.from({ length: 3 }, (_, i) => String(anoAtual - i));

  function handleSave() {
    const itens = laudosDisponiveis
      .filter((l) => selecionados[l.id] !== undefined)
      .map((l) => ({
        laudo_id: l.id,
        laudo_id_texto: l.laudoId ?? undefined,
        placa: l.veiculo?.placa ?? undefined,
        tipo_veiculo: l.veiculo?.tipo ?? "auto_leve",
        valor_unitario: Number(selecionados[l.id]) || 0,
        descricao: undefined,
      }));

    onSave({
      parceiro_id: parceiroId,
      mes: Number(mes),
      ano: Number(ano),
      valor_total: valorTotal,
      vencimento,
      observacoes: observacoes || undefined,
      chave_pix: chavePix || undefined,
      itens,
    });
  }

  function handleParceiroChange(id: string) {
    setParceiroId(id);
    setSelecionados({});
  }

  const temItens = Object.keys(selecionados).length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Fatura para Parceiro</DialogTitle>
          <DialogDescription>
            Selecione os laudos concluídos para incluir na cobrança
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Parceiro + período */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1">
              <Label>Parceiro *</Label>
              <Select value={parceiroId} onValueChange={handleParceiroChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o parceiro" />
                </SelectTrigger>
                <SelectContent>
                  {parceiros.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      Nenhum parceiro cadastrado
                    </SelectItem>
                  )}
                  {parceiros.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nomeFantasia || p.razaoSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Mês de referência *</Label>
              <Select value={mes} onValueChange={setMes}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESES.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Ano *</Label>
              <Select value={ano} onValueChange={setAno}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anos.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Vencimento *</Label>
              <Input
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
              />
            </div>
          </div>

          {/* Importar do Lote */}
          {parceiroId && lotesDosParceiro.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Importar laudos de um lote fechado</Label>
              <div className="flex flex-wrap gap-2">
                {lotesDosParceiro.map((lote) => (
                  <button
                    key={lote.id}
                    type="button"
                    onClick={() => setLoteImportadoId(lote.id)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      loteImportadoId === lote.id
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {MESES[lote.mes - 1]} {lote.ano} · {lote.qtdLaudos} laudos
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Seleção de laudos */}
          {parceiroId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Laudos disponíveis</Label>
                <div className="flex items-center gap-2">
                  <Input
                    className="h-7 w-28 text-sm"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="R$ padrão"
                    value={valorPadrao}
                    onChange={(e) => setValorPadrao(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={
                      !valorPadrao || Object.keys(selecionados).length === 0
                    }
                    onClick={aplicarValorPadrao}
                  >
                    Aplicar a todos
                  </Button>
                </div>
              </div>

              {loadingLaudos ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : laudosDisponiveis.length === 0 ? (
                <div className="border rounded-lg py-8 text-center text-muted-foreground">
                  <PackageSearch className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    Nenhum laudo disponível para faturar
                  </p>
                  <p className="text-xs mt-1">
                    Laudos precisam estar assinados ou finalizados
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {laudosDisponiveis.map((laudo) => {
                    const isSelecionado = selecionados[laudo.id] !== undefined;
                    return (
                      <div
                        key={laudo.id}
                        className={`flex items-center gap-3 px-3 py-2.5 ${isSelecionado ? "bg-primary/5" : ""}`}
                      >
                        <Checkbox
                          checked={isSelecionado}
                          onCheckedChange={() => handleToggleLaudo(laudo)}
                        />
                        <Car className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium font-mono">
                            {laudo.veiculo?.placa || "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {TIPO_VEICULO_LABELS[laudo.veiculo?.tipo ?? ""] ||
                              laudo.veiculo?.tipo ||
                              "—"}
                            {laudo.laudoId && <> · {laudo.laudoId}</>}
                            {laudo.dataVistoria && (
                              <>
                                {" "}
                                ·{" "}
                                {new Date(
                                  laudo.dataVistoria,
                                ).toLocaleDateString("pt-BR")}
                              </>
                            )}
                          </p>
                        </div>
                        {isSelecionado && (
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              R$
                            </span>
                            <Input
                              className="h-7 w-24 text-sm"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0,00"
                              value={selecionados[laudo.id]}
                              onChange={(e) =>
                                handleValorItem(laudo.id, e.target.value)
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label>Chave PIX</Label>
            <Input
              placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
              value={chavePix}
              onChange={(e) => setChavePix(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Pré-preenchida com a chave PIX cadastrada em Configurações. Aparece com QR Code na fatura.
            </p>
          </div>

          <div className="space-y-1">
            <Label>Observações</Label>
            <Textarea
              placeholder="Informações adicionais para o parceiro..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <span className="font-medium">
              Total ({Object.keys(selecionados).length} laudo
              {Object.keys(selecionados).length !== 1 ? "s" : ""})
            </span>
            <span className="text-xl font-bold">
              {formatCurrency(valorTotal)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!parceiroId || !temItens || valorTotal === 0 || loading}
            onClick={handleSave}
            className="gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Emitir Fatura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FaturasParceiro() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { profile } = useAuth();
  const anoAtual = new Date().getFullYear();

  const isParceiro = profile?.empresa?.tipo === "PARCEIRO_DE_NEGOCIOS";
  const isVistoriadora = profile?.empresa?.tipo === "VISTORIADORA";

  // Suporte a abertura automática via ?lote_id=xxx (vindo da tela de Lotes)
  const searchParams = new URLSearchParams(searchString);
  const initialLoteId = searchParams.get("lote_id") ?? undefined;

  const { data: loteInicial } = useQuery<{ parceiroId: string | null; mes: number; ano: number } | null>({
    queryKey: ["lote-inicial", initialLoteId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/lotes/${initialLoteId}`);
      return res.json();
    },
    enabled: !!initialLoteId,
  });

  const [filtroAno, setFiltroAno] = useState(String(anoAtual));
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [showNova, setShowNova] = useState(() => !!initialLoteId);
  const [faturaParaExcluir, setFaturaParaExcluir] =
    useState<FaturaParceiro | null>(null);
  const [faturaDetalhe, setFaturaDetalhe] = useState<FaturaParceiro | null>(null);
  const anos = Array.from({ length: 3 }, (_, i) => String(anoAtual - i));

  const { data: faturas = [], isLoading } = useQuery<FaturaParceiro[]>({
    queryKey: ["faturas-parceiro", filtroAno, filtroStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ ano: filtroAno });
      if (filtroStatus !== "todos") params.set("status", filtroStatus);
      const res = await apiRequest("GET", `/api/faturas-parceiro?${params}`);
      return res.json();
    },
  });

  const { data: parceiros = [] } = useQuery<Empresa[]>({
    queryKey: ["parceiros-da-vistoriadora"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/faturas-parceiro/parceiros");
        return res.json();
      } catch {
        return [];
      }
    },
    enabled: isVistoriadora,
  });

  const criarFatura = useMutation({
    mutationFn: (data: any) =>
      apiRequest("POST", "/api/faturas-parceiro", data).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faturas-parceiro"] });
      queryClient.invalidateQueries({
        queryKey: ["laudos-disponiveis-faturamento"],
      });
      setShowNova(false);
      toast({ title: "Fatura emitida com sucesso!" });
    },
    onError: () =>
      toast({ title: "Erro ao emitir fatura", variant: "destructive" }),
  });

  const marcarPago = useMutation({
    mutationFn: (id: string) =>
      apiRequest("PATCH", `/api/faturas-parceiro/${id}/pagar`).then((r) =>
        r.json(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faturas-parceiro"] });
      toast({ title: "Fatura marcada como paga!" });
    },
    onError: () =>
      toast({ title: "Erro ao atualizar", variant: "destructive" }),
  });

  const cancelarFatura = useMutation({
    mutationFn: (id: string) =>
      apiRequest("PATCH", `/api/faturas-parceiro/${id}/cancelar`).then((r) =>
        r.json(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faturas-parceiro"] });
      toast({ title: "Fatura cancelada." });
    },
    onError: () => toast({ title: "Erro ao cancelar", variant: "destructive" }),
  });

  const excluirFatura = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/faturas-parceiro/${id}`).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faturas-parceiro"] });
      queryClient.invalidateQueries({
        queryKey: ["laudos-disponiveis-faturamento"],
      });
      setFaturaParaExcluir(null);
      toast({ title: "Fatura excluída." });
    },
    onError: () => toast({ title: "Erro ao excluir", variant: "destructive" }),
  });

  const totalPendente = faturas
    .filter((f) => f.status === "pendente")
    .reduce((acc, f) => acc + Number(f.valorTotal), 0);

  const totalRecebido = faturas
    .filter((f) => f.status === "pago")
    .reduce((acc, f) => acc + Number(f.valorTotal), 0);

  return (
    <Shell>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {isParceiro ? "Faturas Recebidas" : "Faturas para Parceiros"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isParceiro
                ? "Faturas emitidas pela sua vistoriadora"
                : "Gerencie as cobranças emitidas para seus parceiros de negócios"}
            </p>
          </div>
          {isVistoriadora && (
            <Button
              onClick={() => setShowNova(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Nova Fatura
            </Button>
          )}
        </div>

        {/* Resumo (só Vistoriadora) */}
        {isVistoriadora && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">A receber</p>
                    <p className="text-xl font-bold text-orange-600">
                      {formatCurrency(totalPendente)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Recebido em {filtroAno}
                    </p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(totalRecebido)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros */}
        <div className="flex gap-3 flex-wrap">
          <Select value={filtroAno} onValueChange={setFiltroAno}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {anos.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                <SelectItem key={v} value={v}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : faturas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Nenhuma fatura encontrada</p>
              {isVistoriadora && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowNova(true)}
                >
                  Emitir primeira fatura
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {faturas.map((fatura) => {
              const StatusIcon = STATUS_CONFIG[fatura.status]?.icon ?? Clock;
              return (
                <Card
                  key={fatura.id}
                  className="hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => setFaturaDetalhe(fatura)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold">
                            FP-{fatura.ano}-
                            {String(fatura.numero).padStart(4, "0")}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {MESES[fatura.mes - 1]}
                            {" · "}
                            {isParceiro
                              ? `Emitida por: ${fatura.vistoriadora?.nomeFantasia || fatura.vistoriadora?.razaoSocial}`
                              : `Para: ${fatura.parceiro?.nomeFantasia || fatura.parceiro?.razaoSocial}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Venc.: {formatDate(fatura.vencimento)}
                            {" · "}
                            {fatura.itens?.length ?? 0} laudo(s)
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-bold text-lg">
                          {formatCurrency(Number(fatura.valorTotal))}
                        </p>
                        <Badge
                          variant={
                            STATUS_CONFIG[fatura.status]?.variant ?? "outline"
                          }
                          className="gap-1"
                        >
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_CONFIG[fatura.status]?.label ?? fatura.status}
                        </Badge>

                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() =>
                              setLocation(
                                `/faturas-parceiro/${fatura.id}/relatorio`,
                              )
                            }
                          >
                            <Printer className="w-3 h-3" />
                            Relatório
                          </Button>

                          {isVistoriadora &&
                            ["pendente", "atrasada"].includes(
                              fatura.status,
                            ) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="w-8 h-8 p-0"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => marcarPago.mutate(fatura.id)}
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                                    Marcar como pago
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      cancelarFatura.mutate(fatura.id)
                                    }
                                  >
                                    <XCircle className="w-4 h-4 mr-2 text-muted-foreground" />
                                    Cancelar fatura
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => setFaturaParaExcluir(fatura)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Excluir
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <FaturaDetalheDialog
        fatura={faturaDetalhe}
        onClose={() => setFaturaDetalhe(null)}
        onVerRelatorio={(id) => setLocation(`/faturas-parceiro/${id}/relatorio`)}
      />

      {isVistoriadora && (
        <NovaFaturaDialog
          open={showNova}
          onClose={() => setShowNova(false)}
          parceiros={parceiros}
          onSave={(data) => criarFatura.mutate(data)}
          loading={criarFatura.isPending}
          initialLoteId={initialLoteId}
          initialParceiroId={loteInicial?.parceiroId ?? undefined}
        />
      )}

      <AlertDialog
        open={!!faturaParaExcluir}
        onOpenChange={() => setFaturaParaExcluir(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir fatura?</AlertDialogTitle>
            <AlertDialogDescription>
              A fatura FP-{faturaParaExcluir?.ano}-
              {String(faturaParaExcluir?.numero ?? "").padStart(4, "0")} será
              excluída permanentemente e os laudos voltarão a estar disponíveis.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                faturaParaExcluir && excluirFatura.mutate(faturaParaExcluir.id)
              }
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
